import { ThreeElements, useThree } from '@react-three/fiber';
import React, { FunctionComponent, memo, useLayoutEffect, useRef } from 'react';
import { useSceneStore, useUserStore } from '../zustand';
import { ITransCatTypes, Transition } from '@r3f/transitions';
import mergeRefs from '@r3f/utils/general';
import { Group, Object3D } from 'three';

type IFadeGroupProps = ThreeElements['group'] & {
	transition: Transition<number>;
	sceneLevel?: boolean;
	transitionCategory: ITransCatTypes;
	screenRelativeEntityLevel?: boolean;
	sceneId?: string;
};

const FadeGroup: FunctionComponent<IFadeGroupProps> = React.forwardRef(
	({ children, transition, transitionCategory, screenRelativeEntityLevel = false, sceneLevel = false, sceneId, ...props }, ref) => {
		const { scene } = useThree();
		const groupRef = useRef<Group>(null);
		const innerGroupRef = useRef<Group>(null);
		// This is applicable for Face Tracked scenes, with entities attached to the 'origin' landmark
		const originGroupRef = useRef<Object3D | null | undefined>(null);
		const isFaceTrackingVisible = useUserStore((state) => state.isFaceTrackingVisible);
		const activeSceneId = useSceneStore((state) => state.activeSceneId);

		// Update the face-tracked, origin-landmark group as it is outside the Scene fade group and needs to be traversed in the useLayoutEffect
		useLayoutEffect(() => {
			if (activeSceneId) {
				if(scene?.getObjectByName(`${activeSceneId}_origin`)) originGroupRef.current = scene?.getObjectByName(`${activeSceneId}_origin`);
			}
		}, [activeSceneId, isFaceTrackingVisible, scene, transitionCategory]);

		useLayoutEffect(() => {
			if (screenRelativeEntityLevel) return;
			const group = groupRef.current!;
			if (!sceneLevel) {
				// entity level transition
				transition.onChange((entityOpacity) => {
					group.traverse((node: any) => {
						if (node?.material && node?.material?.userData && node?.material?.userData?.entityTransition) {
							//if (debug) console.log('apply opacity: ', node.material.userData.opacity, opacity, id)
							const adjEntityOpacity = node.material.userData.entityTransition.originalOpacity * entityOpacity;
							// `sceneOpacity` is only ever written by the SCENE fade group's traversals, so a
							// material that appeared after the scene transition finished has none — and an
							// entity added during a live preview session is exactly that. Without the default
							// this multiplied by `undefined`, hit the NaN guard, and left the material at the 0
							// that `onStart` set: a new entity that never became visible. It used to be masked
							// by the scene transition replaying on every edit, which is no longer the case.
							// Having no recorded scene opacity means no scene fade has touched it, so 1 is right.
							const sceneOpacity = node.material.userData.sceneOpacity ?? 1;
							if (isNaN(sceneOpacity * adjEntityOpacity)) return;
							node.material.transparent = true;
							node.material.opacity = sceneOpacity * adjEntityOpacity;
						}
					});
				});
				transition.onStart((entityStartOpacity) => {
					// console.log('onStart in entity logic');
					if (transitionCategory === ITransCatTypes.enter) (innerGroupRef.current as Group).visible = false;
					group.traverse((node: any) => {
						if (node?.material && node?.material?.userData) {
							// clean up entity transition state on transition start
							if (typeof node.material.userData.entityTransition === 'undefined') {
								node.material.userData.entityTransition = { originalOpacity: node.material.opacity };
								node.material.opacity = 0; // set opacity to 0 in case onChange runs before onStart finishes
							}
							const adjEntityStartOpacity = node.material.userData.entityTransition.originalOpacity * entityStartOpacity;
							node.material.userData.entityTransition.hasFinished = false;
							node.material.userData.entityTransition.opacity = adjEntityStartOpacity;
							node.material.transparent = true;
						}
					});
					// set inner group to visible once initial opacities are set to avoid initial entity
					// flashes due to wrong opacities
					setTimeout(() => (innerGroupRef.current as Group).visible = true, 0);
				});
				transition.onFinish((entityEndOpacity) => {
					group.traverse((node: any) => {
						if (node?.material && node?.material?.userData && node?.material?.userData?.entityTransition) {
							// store entity transition progress and opacity to use on scene level if
							// entity transition finishes before the scene transtion
							const adjEntityEndOpacity = node.material.userData.entityTransition.originalOpacity * entityEndOpacity;
							node.material.userData.entityTransition.hasFinished = true;
							node.material.userData.entityTransition.opacity = adjEntityEndOpacity;
						}
					});
				});
			} else {
				// scene level transition
				transition.onChange((sceneOpacity) => {
					// Face-tracked, origin-landmark group	
					if (originGroupRef.current) {
						originGroupRef.current.traverse((node: any) => {
							if (!node?.material) return;
							// if entity transition has finished before the scene transition, apply
							// the combined scene and entity transition opacity to material
							if (node.material.userData?.entityTransition?.hasFinished) {
								node.material.opacity = sceneOpacity * node.material.userData.entityTransition.opacity;
								return;
							}
							node.material.userData.sceneOpacity = sceneOpacity;
						});
					}
					// Everything else in scene is within this group
					group.traverse((node: any) => {
						if (!node?.material) return;
						// if entity transition has finished before the scene transition, apply
						// the combined scene and entity transition opacity to material
						if (node.material.userData?.entityTransition?.hasFinished) {
							node.material.opacity = sceneOpacity * node.material.userData.entityTransition.opacity;
							return;
						}
						node.material.userData.sceneOpacity = sceneOpacity;
					});
				});

				transition.onFinish((sceneEndOpacity) => {
					// console.log('onFinish in scene logic');
					// Face-tracked, origin-landmark group
					if (originGroupRef.current) {
						originGroupRef.current.traverse((node: any) => {
							if (!node?.material) return;
							// store final scene opacity for entity transitions to use in new transition
							node.material.userData.sceneOpacity = sceneEndOpacity;
						});
					}
					// Everything else in scene is within this group
					group.traverse((node: any) => {
						if (!node?.material) return;
						// store final scene opacity for entity transitions to use in new transition
						node.material.userData.sceneOpacity = sceneEndOpacity;
					});
				});
			}
		}, [groupRef, transition, sceneLevel, transitionCategory, screenRelativeEntityLevel, originGroupRef]);

		return (
			<group name="FADE GROUP" ref={mergeRefs([groupRef, ref])} {...props}>
				<group name="FADE GROUP INNER" ref={innerGroupRef} visible={sceneLevel || screenRelativeEntityLevel ? true : false}>
					{children}
				</group>
			</group>
		);
	}
) as FunctionComponent<IFadeGroupProps>;
FadeGroup.displayName = 'FadeGroup';
export default memo(FadeGroup);
